import { useAuth } from '@clerk/clerk-react';
import {
  CreateAbstractScreeningInput,
  CreateAndExecuteLiteratureReviewSearchesInput,
  CreateCriteriaInput,
  CreateLiteratureReviewInput,
  DeleteLiteratureSearchInput,
  ExecuteAbstractScreeningInput,
  ExecuteFullTextScreeningInput,
  GenerateCriteriaInput,
  GenerateFullTextCriteriaInput,
  GetAbstractScreeningResultsFilterParams,
  GetAbstractScreeningResultsResponse,
  GetFullTextScreeningResultsFilterParams,
  GetFullTextScreeningResultsResponse,
  SearchPapersInput,
  UpdateCriteriaInput,
  UpdateFullTextScreeningCriteriaResultInput,
  UpdateLiteratureReviewInput,
  UpdatePaperScreeningResultJustification,
  UpdatePaperScreeningScreeningResultInput,
  UpdatePaperScreeningValidationStatusInput,
  UpsertLiteratureReviewSearchesInput,
} from '@ditto/shared';
import {
  UseQueryOptions,
  skipToken,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  useAuthenticatedClient,
  useProtectedQuery,
  useWorkspaceBaseUrl,
} from './useApi';
export const queryKeys = {
  all: ['literatureReviews'],
  allLiteratureReviews: () => [...queryKeys.all, 'literatureReviews'],
  literatureReview: (id: string | undefined) => [
    ...queryKeys.allLiteratureReviews(),
    'literatureReview',
    id ?? '',
  ],
  searchPapers: (query: string) => [...queryKeys.all, 'searchPapers', query],
  allAbstractScreenings: () => [...queryKeys.all, 'allAbstractScreenings'],
  getAbstractScreeningQueryKey: (id: string | undefined) => [
    ...queryKeys.allAbstractScreenings(),
    'abstractScreening',
    id,
  ],
  allAbstractScreeningResults: () => [
    ...queryKeys.allAbstractScreenings(),
    'allAbstractScreeningResults',
  ],
  getAbstractScreeningResultsQueryKey: (
    id: string | undefined,
    filters?: GetAbstractScreeningResultsFilterParams
  ) => [
    ...queryKeys.allAbstractScreeningResults(),
    'abstractScreeningResults',
    id,
    ...(filters ? ['filters', filters] : []),
  ],
  allLiteratureReviewSearches: () => [
    ...queryKeys.all,
    'literatureReviewSearches',
  ],
  literatureReviewSearches: (literatureReviewId: string | undefined) => [
    ...queryKeys.allLiteratureReviewSearches(),
    literatureReviewId,
  ],
  allFullTextScreenings: () => [...queryKeys.all, 'allFullTextScreenings'],
  getFullTextScreeningQueryKey: (id: string | undefined) => [
    ...queryKeys.allFullTextScreenings(),
    'fullTextScreening',
    id,
  ],
  allFullTextScreeningResults: () => [
    ...queryKeys.allFullTextScreenings(),
    'allFullTextScreeningResults',
  ],
  getFullTextScreeningResultsQueryKey: (
    id: string | undefined,
    filters?: GetFullTextScreeningResultsFilterParams
  ) => [
    ...queryKeys.allFullTextScreeningResults(),
    'fullTextScreeningResults',
    id,
    ...(filters ? ['filters', filters] : []),
  ],
};

export function useListLiteratureReviews() {
  const { client } = useAuthenticatedClient();
  return useProtectedQuery({
    queryKey: queryKeys.allLiteratureReviews(),
    queryFn: async () => {
      const response = await client.literatureReview.listLiteratureReviews();
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch literature reviews');
    },
  });
}

export function useGetLiteratureReview(literatureReviewId: string | undefined) {
  const { client } = useAuthenticatedClient();
  return useProtectedQuery({
    queryKey: queryKeys.literatureReview(literatureReviewId),
    queryFn: literatureReviewId
      ? async () => {
          const response = await client.literatureReview.getLiteratureReview({
            params: { id: literatureReviewId },
          });
          if (response.status === 200) {
            return response.body;
          }
          throw new Error('Failed to fetch literature review');
        }
      : skipToken,
  });
}

export function useUpdateLiteratureReview() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      literatureReviewId,
      data,
    }: {
      literatureReviewId: string;
      data: UpdateLiteratureReviewInput;
    }) => {
      const response = await client.literatureReview.updateLiteratureReview({
        params: { id: literatureReviewId },
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to create literature review');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.allLiteratureReviews(),
      });
    },
  });
}

export function useSearchPapers() {
  const { client } = useAuthenticatedClient();

  return useMutation({
    mutationFn: async (data: SearchPapersInput) => {
      const response = await client.literatureReview.searchPapers({
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to search papers');
    },
  });
}

export function useCreateLiteratureReview() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateLiteratureReviewInput) => {
      const response = await client.literatureReview.createLiteratureReview({
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to create literature review');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.allLiteratureReviews(),
      });
    },
  });
}

export function useGetAbstractScreeningByIdApi(id: string | undefined) {
  const { client } = useAuthenticatedClient();
  return useProtectedQuery({
    queryKey: queryKeys.getAbstractScreeningQueryKey(id),
    queryFn: id
      ? async () => {
          const response = await client.literatureReview.getAbstractScreening({
            params: { id },
          });
          if (response.status === 200) {
            return response.body;
          }
          throw new Error('Failed to fetch abstract screening');
        }
      : skipToken,
  });
}

export function useCreateAbstractScreening() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      literatureReviewId,
      data,
    }: {
      literatureReviewId: string;
      data: CreateAbstractScreeningInput;
    }) => {
      const response = await client.literatureReview.createAbstractScreening({
        params: {
          id: literatureReviewId,
        },
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to create abstract screening');
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.allLiteratureReviews(),
      });
    },
  });
}

export function useExecuteAbstractScreening() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      literatureReviewId,
      data,
    }: {
      literatureReviewId: string;
      data: ExecuteAbstractScreeningInput;
    }) => {
      const response = await client.literatureReview.executeAbstractScreening({
        params: { id: literatureReviewId },
        body: data,
      });

      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to execute abstract screening');
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.allAbstractScreeningResults(),
      });
    },
  });
}

export function useGetAbstractScreeningResultsByIdApi(
  id: string | undefined,
  filters?: GetAbstractScreeningResultsFilterParams,
  options?: {
    refetchInterval?: UseQueryOptions<
      GetAbstractScreeningResultsResponse,
      Error,
      GetAbstractScreeningResultsResponse,
      unknown[]
    >['refetchInterval'];
  }
) {
  const { client } = useAuthenticatedClient();
  return useProtectedQuery({
    queryKey: queryKeys.getAbstractScreeningResultsQueryKey(id, filters),
    queryFn: id
      ? async () => {
          const response =
            await client.literatureReview.getAbstractScreeningResults({
              params: { id },
              query: filters,
            });
          if (response.status === 200) {
            return response.body;
          }
          throw new Error('Failed to fetch abstract screening results');
        }
      : skipToken,
    ...options,
  });
}

export function useCreateAndExecuteLiteratureReviewSearches() {
  const { client } = useAuthenticatedClient();

  return useMutation({
    mutationFn: async ({
      literatureReviewId,
      data,
    }: {
      literatureReviewId: string;
      data: CreateAndExecuteLiteratureReviewSearchesInput;
    }) => {
      const response =
        await client.literatureReview.createAndExecuteLiteratureReviewSearches({
          params: { id: literatureReviewId },
          body: data,
        });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error(
        'Failed to create and execute literature review searches'
      );
    },
  });
}

export const useGetSearchResultsApi = (literatureReviewId: string) => {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: ['searchResults', literatureReviewId],
    queryFn: async () => {
      const response = await client.literatureReview.getSearchResults({
        params: { id: literatureReviewId },
      });

      if (response.status === 200) {
        return response.body;
      }

      throw new Error('Failed to fetch search results');
    },
    enabled: !!literatureReviewId,
  });
};

export const useGetLiteratureSearchesApi = (literatureReviewId: string) => {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: ['literatureSearches', literatureReviewId],
    queryFn: async () => {
      const response = await client.literatureReview.getLiteratureSearches({
        params: { id: literatureReviewId },
      });

      if (response.status === 200) {
        return response.body;
      }

      throw new Error('Failed to fetch literature searches');
    },
    enabled: !!literatureReviewId,
  });
};

export const useExportLiteratureReviewApi = (
  literatureReviewId: string | undefined
) => {
  const { client } = useAuthenticatedClient();

  return useMutation({
    mutationFn: async (format: 'xlsx' | 'csv') => {
      if (!literatureReviewId) {
        return undefined;
      }

      const response = await client.literatureReview.exportLiteratureReview({
        params: { id: literatureReviewId },
        body: { format },
      });

      if (response.status === 200) {
        return response.body;
      }

      throw new Error('Failed to export literature review');
    },
  });
};

export const useUpdatePaperScreeningResultApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      paperScreeningId,
      data,
    }: {
      paperScreeningId: string;
      data: UpdatePaperScreeningScreeningResultInput;
    }) => {
      const response = await client.literatureReview.updatePaperScreeningResult(
        {
          params: { paperScreeningId },
          body: data,
        }
      );
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to update paper screening result');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.allAbstractScreeningResults(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.allFullTextScreenings(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.allLiteratureReviews(),
      });
    },
  });
};

export const useUpdatePaperScreeningValidationStatusApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      paperScreeningId,
      data,
    }: {
      paperScreeningId: string;
      data: UpdatePaperScreeningValidationStatusInput;
    }) => {
      const response =
        await client.literatureReview.updatePaperScreeningValidationStatus({
          params: { paperScreeningId },
          body: data,
        });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to update paper screening validation status');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.allAbstractScreeningResults(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.allLiteratureReviews(),
      });
    },
  });
};

export const useBulkValidatePaperScreeningsApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (paperScreeningIds: string[]) => {
      const response =
        await client.literatureReview.bulkValidatePaperScreenings({
          body: { paperScreeningIds },
        });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to bulk validate paper screenings');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.allFullTextScreeningResults(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.allAbstractScreeningResults(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.allLiteratureReviews(),
      });
    },
  });
};

export const useUpdatePaperScreeningResultJustificationApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      paperScreeningId,
      data,
    }: {
      paperScreeningId: string;
      data: UpdatePaperScreeningResultJustification;
    }) => {
      const response =
        await client.literatureReview.updatePaperScreeningResultJustification({
          params: { paperScreeningId },
          body: data,
        });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to update paper screening result justification');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.allAbstractScreeningResults(),
      });
    },
  });
};

export const useLiteratureReviewSearchesApi = (
  literatureReviewId: string | undefined
) => {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: queryKeys.literatureReviewSearches(literatureReviewId),
    queryFn: literatureReviewId
      ? async () => {
          const response = await client.literatureReview.getLiteratureSearches({
            params: { id: literatureReviewId },
          });

          if (response.status === 200) {
            return response.body;
          }

          throw new Error('Failed to fetch literature review searches');
        }
      : skipToken,
  });
};

export const useUpsertAndExecuteLiteratureReviewSearchesApi = (
  literatureReviewId: string | undefined
) => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: literatureReviewId
      ? async (data: UpsertLiteratureReviewSearchesInput) => {
          const response =
            await client.literatureReview.upsertLiteratureReviewSearches({
              params: { id: literatureReviewId },
              body: data,
            });
          if (response.status === 200) {
            return response.body;
          }
          throw new Error('Failed to upsert literature review searches');
        }
      : undefined,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.allLiteratureReviewSearches(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.allLiteratureReviews(),
      });
    },
  });
};

export const useDeleteLiteratureSearchApi = (
  literatureReviewId: string | undefined
) => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: literatureReviewId
      ? async (data: DeleteLiteratureSearchInput) => {
          const response = await client.literatureReview.deleteLiteratureSearch(
            {
              params: { id: literatureReviewId },
              body: data,
            }
          );
          if (response.status === 200) {
            return response.body;
          }
          throw new Error('Failed to delete literature search');
        }
      : undefined,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.allLiteratureReviewSearches(),
      });
    },
  });
};

export const useExportLiteratureSearchesApi = (
  literatureReviewId: string | undefined
) => {
  const { client } = useAuthenticatedClient();

  return useMutation({
    mutationFn: literatureReviewId
      ? async () => {
          const response =
            await client.literatureReview.exportLiteratureSearches({
              params: { id: literatureReviewId },
            });
          if (response.status === 200) {
            return response.body;
          }
          throw new Error('Failed to export literature review searches');
        }
      : undefined,
  });
};

export const useGenerateCriteriaApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      literatureReviewId,
      data,
    }: {
      literatureReviewId: string;
      data: GenerateCriteriaInput;
    }) => {
      const response = await client.literatureReview.generateCriteria({
        params: { id: literatureReviewId },
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to generate criteria');
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.allAbstractScreenings(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.allLiteratureReviews(),
      });
    },
  });
};

export const useCreateCriteriaApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCriteriaInput) => {
      const response = await client.literatureReview.createCriteria({
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to create criteria');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.allAbstractScreenings(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.allLiteratureReviews(),
      });
    },
  });
};

export const useUpdateCriteriaApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      criteriaId,
      data,
    }: {
      criteriaId: string;
      data: UpdateCriteriaInput;
    }) => {
      const response = await client.literatureReview.updateCriteria({
        params: { criteriaId },
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to create criteria');
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.allAbstractScreenings(),
      });
    },
  });
};

export const useDeleteCriteriaApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ criteriaId }: { criteriaId: string }) => {
      const response = await client.literatureReview.deleteCriteria({
        params: { criteriaId },
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to delete criteria');
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.allAbstractScreenings(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.allLiteratureReviews(),
      });
    },
  });
};

export const useGenerateFullTextCriteriaApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      literatureReviewId,
      data,
    }: {
      literatureReviewId: string;
      data: GenerateFullTextCriteriaInput;
    }) => {
      const response = await client.literatureReview.generateFullTextCriteria({
        params: { id: literatureReviewId },
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to generate full text criteria');
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.allFullTextScreenings(),
      });
    },
  });
};

export const useUpsertFullTextScreeningApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      literatureReviewId,
    }: {
      literatureReviewId: string;
    }) => {
      const response = await client.literatureReview.upsertFullTextScreening({
        params: { id: literatureReviewId },
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to upsert full text screening');
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.allFullTextScreenings(),
      });
    },
  });
};

export const useGetFullTextScreeningApi = (
  literatureReviewId: string | undefined
) => {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: queryKeys.getFullTextScreeningQueryKey(literatureReviewId),
    queryFn: literatureReviewId
      ? async () => {
          const response = await client.literatureReview.getFullTextScreening({
            params: { id: literatureReviewId },
          });
          if (response.status === 200) {
            return response.body;
          }
          throw new Error('Failed to fetch full text screening');
        }
      : skipToken,
  });
};

export const useGetFullTextScreeningResultsApi = (
  literatureReviewId: string | undefined,
  filters?: GetFullTextScreeningResultsFilterParams,
  options?: {
    refetchInterval?: UseQueryOptions<
      GetFullTextScreeningResultsResponse,
      Error,
      GetFullTextScreeningResultsResponse,
      unknown[]
    >['refetchInterval'];
  }
) => {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: queryKeys.getFullTextScreeningResultsQueryKey(
      literatureReviewId,
      filters
    ),
    queryFn: literatureReviewId
      ? async () => {
          const response =
            await client.literatureReview.getFullTextScreeningResults({
              params: { id: literatureReviewId },
              query: filters,
            });
          if (response.status === 200) {
            return response.body;
          }
          throw new Error('Failed to fetch full text screening results');
        }
      : skipToken,
    ...options,
  });
};

export const useExportFullTextScreeningResultsApi = (
  literatureReviewId: string | undefined
) => {
  const { client } = useAuthenticatedClient();

  return useMutation({
    mutationFn: literatureReviewId
      ? async (format: 'xlsx' | 'csv') => {
          const response =
            await client.literatureReview.exportFullTextScreeningResults({
              params: { id: literatureReviewId },
              body: { format },
            });
          if (response.status === 200) {
            return response.body;
          }
          throw new Error('Failed to export full text screening results');
        }
      : undefined,
  });
};

export const useExecuteFullTextScreeningApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      literatureReviewId,
      data,
    }: {
      literatureReviewId: string;
      data: ExecuteFullTextScreeningInput;
    }) => {
      const response = await client.literatureReview.executeFullTextScreening({
        params: { id: literatureReviewId },
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to execute full text screening');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.allFullTextScreenings(),
      });
    },
  });
};

export const useUpdateFullTextScreeningCriteriaResultApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateFullTextScreeningCriteriaResultInput) => {
      const response =
        await client.literatureReview.updateFullTextScreeningCriteriaResult({
          body: data,
        });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to update full text screening criteria result');
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.allFullTextScreeningResults(),
      });
    },
  });
};

export const useUploadWorkPdfApi = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const workspaceBaseUrl = useWorkspaceBaseUrl();

  return useMutation({
    mutationFn: async ({
      file,
      paperScreeningId,
    }: {
      file: File;
      paperScreeningId: string;
    }) => {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', file);

      const url = `${workspaceBaseUrl}/literature-reviews/paper-screenings/${paperScreeningId}/upload-pdf`;

      // For file uploads, we need to use fetch directly since ts-rest doesn't handle FormData well
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Upload failed');
      }

      return response.json();
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.allFullTextScreeningResults(),
      });
    },
  });
};
